import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
    as?: 'div' | 'section' | 'li'
}

/**
 * Fades content in when it scrolls into view. Content is always rendered on the
 * server, and anything already on screen at load is left untouched.
 */
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0, as = 'div' }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [state, setState] = useState<'static' | 'hidden' | 'shown'>('static')

    useEffect(() => {
        const element = ref.current

        if (!element || typeof IntersectionObserver === 'undefined') return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        if (element.getBoundingClientRect().top < window.innerHeight * 0.92) return

        setState('hidden')

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setState('shown')
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const Element = as

    return (
        <Element
            ref={ref as never}
            className={cn(
                className,
                state === 'hidden' && 'opacity-0',
                state === 'shown' && 'animate-in fade-in slide-in-from-bottom-6 duration-700 ease-out fill-mode-both'
            )}
            style={state === 'shown' && delay ? { animationDelay: `${delay}ms` } : undefined}
        >
            {children}
        </Element>
    )
}
